/**
 * useNicknames — read/write the caller's private OrchistNickname overlay.
 *
 * Phase G.7.7 (sacred/nicknames.md):
 *   Each Orchist may privately nickname any other Orchist they recognize.
 *   The nickname is visible ONLY to the assigning Orchist (RLS-enforced).
 *   The public identity (publicIdentityString) remains untouched.
 *
 * Returns:
 *   - byTargetId: Map<targetContactId, NicknameRow> for fast inline lookup
 *   - rows: array of all nicknames (paginated for the manager surface)
 *   - search(query): filter helper for the manager
 *   - setNickname / clearNickname: write operations via RPC
 *
 * Pagination is intentional even for nicknames — at scale, an Orchist
 * may nickname thousands of others. Cursor-based, default pageSize=50.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface NicknameRow {
  id: string;
  ownerContactId: string;
  targetContactId: string;
  nickname: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const NICKNAME_COLUMNS =
  'id, ownerContactId, targetContactId, nickname, notes, createdAt, updatedAt';

const DEFAULT_PAGE_SIZE = 50;

interface UseNicknamesOptions {
  pageSize?: number;
}

export function useNicknames(options: UseNicknamesOptions = {}) {
  const pageSize = options.pageSize ?? DEFAULT_PAGE_SIZE;
  const { contact } = useAuth();
  const [rows, setRows] = useState<NicknameRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setPage(0);
    setRefreshKey((k) => k + 1);
  }, []);

  useEffect(() => {
    if (!contact) {
      setRows([]);
      setTotalCount(0);
      setHasMore(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const { data, error: err, count } = await supabase
        .schema('core')
        .from('OrchistNickname')
        .select(NICKNAME_COLUMNS, { count: 'exact' })
        .eq('ownerContactId', contact.id)
        .order('updatedAt', { ascending: false })
        .range(0, pageSize - 1);
      if (cancelled) return;
      if (err) {
        setError(new Error(err.message));
        setRows([]);
        setTotalCount(null);
        setHasMore(false);
        setLoading(false);
        return;
      }
      const initial = (data ?? []) as NicknameRow[];
      setRows(initial);
      setTotalCount(count ?? initial.length);
      setHasMore((count ?? initial.length) > initial.length);
      setLoading(false);
      setPage(1);
    })();
    return () => {
      cancelled = true;
    };
  }, [contact, pageSize, refreshKey]);

  const loadMore = useCallback(async () => {
    if (!contact || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const from = page * pageSize;
    const to = from + pageSize - 1;
    const { data, error: err } = await supabase
      .schema('core')
      .from('OrchistNickname')
      .select(NICKNAME_COLUMNS)
      .eq('ownerContactId', contact.id)
      .order('updatedAt', { ascending: false })
      .range(from, to);
    if (err) {
      setError(new Error(err.message));
      setLoadingMore(false);
      return;
    }
    const more = (data ?? []) as NicknameRow[];
    setRows((prev) => {
      const combined = [...prev, ...more];
      setHasMore(totalCount ? combined.length < totalCount : more.length === pageSize);
      return combined;
    });
    setPage((p) => p + 1);
    setLoadingMore(false);
  }, [contact, hasMore, loadingMore, page, pageSize, totalCount]);

  const byTargetId = useMemo(() => {
    const map = new Map<string, NicknameRow>();
    for (const r of rows) map.set(r.targetContactId, r);
    return map;
  }, [rows]);

  const setNickname = useCallback(
    async (targetContactId: string, nickname: string, notes: string | null = null) => {
      const { error: err } = await supabase
        .schema('core')
        .rpc('set_orchist_nickname', {
          p_target_contact_id: targetContactId,
          p_nickname: nickname,
          p_notes: notes,
        });
      if (err) throw new Error(err.message);
      refresh();
    },
    [refresh]
  );

  const clearNickname = useCallback(
    async (targetContactId: string) => {
      const { error: err } = await supabase
        .schema('core')
        .rpc('clear_orchist_nickname', {
          p_target_contact_id: targetContactId,
        });
      if (err) throw new Error(err.message);
      refresh();
    },
    [refresh]
  );

  const search = useCallback(
    (query: string): NicknameRow[] => {
      const q = query.trim().toLowerCase();
      if (!q) return rows;
      return rows.filter(
        (r) =>
          r.nickname.toLowerCase().includes(q) ||
          (r.notes ?? '').toLowerCase().includes(q)
      );
    },
    [rows]
  );

  return {
    rows,
    byTargetId,
    totalCount,
    hasMore,
    loading,
    loadingMore,
    error,
    loadMore,
    refresh,
    setNickname,
    clearNickname,
    search,
  };
}
